import React, { useRef, useEffect } from 'react';

const BIKE_COUNT = 5;
const TRAIL_LENGTH = 120;
const GRID_SIZE = 40;

function randomDir() {
  const dirs = [
    { dx: 1, dy: 0 },
    { dx: -1, dy: 0 },
    { dx: 0, dy: 1 },
    { dx: 0, dy: -1 },
  ];
  return dirs[Math.floor(Math.random() * dirs.length)];
}

function createBike(w, h, colors) {
  const color = colors[Math.floor(Math.random() * colors.length)];
  const dir = randomDir();
  return {
    x: Math.random() * w,
    y: Math.random() * h,
    dx: dir.dx,
    dy: dir.dy,
    speed: 1 + Math.random() * 0.6,
    trail: [],
    color,
    turnTimer: 80 + Math.floor(Math.random() * 140),
  };
}

export default function TronGrid() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let w, h;
    const colors = [
      [13, 148, 136],   // teal-500
      [16, 185, 129],   // emerald-500
      [20, 184, 166],   // teal-400
      [52, 211, 153],   // emerald-400
    ];

    let bikes = [];

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      bikes = Array.from({ length: BIKE_COUNT }, () => createBike(w, h, colors));
    };

    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      // Grid lines
      ctx.lineWidth = 0.5;
      for (let x = 0; x < w; x += GRID_SIZE) {
        ctx.strokeStyle = 'rgba(13,148,136,0.06)';
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += GRID_SIZE) {
        ctx.strokeStyle = 'rgba(13,148,136,0.06)';
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      // Update bikes
      for (const bike of bikes) {
        bike.x += bike.dx * bike.speed;
        bike.y += bike.dy * bike.speed;

        bike.trail.push({ x: bike.x, y: bike.y });
        if (bike.trail.length > TRAIL_LENGTH) bike.trail.shift();

        bike.turnTimer--;
        if (bike.turnTimer <= 0) {
          if (bike.dx !== 0) {
            bike.dy = Math.random() > 0.5 ? 1 : -1;
            bike.dx = 0;
          } else {
            bike.dx = Math.random() > 0.5 ? 1 : -1;
            bike.dy = 0;
          }
          bike.turnTimer = 50 + Math.floor(Math.random() * 120);
        }

        // Wrap edges
        if (bike.x < -30) bike.x = w + 20;
        if (bike.x > w + 30) bike.x = -20;
        if (bike.y < -30) bike.y = h + 20;
        if (bike.y > h + 30) bike.y = -20;

        // Draw trail segments
        const [r, g, b] = bike.color;
        if (bike.trail.length > 1) {
          for (let i = 1; i < bike.trail.length; i++) {
            const t = i / bike.trail.length;
            const alpha = t * 0.18;
            const lw = t * 2.5;
            ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
            ctx.lineWidth = lw;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(bike.trail[i - 1].x, bike.trail[i - 1].y);
            ctx.lineTo(bike.trail[i].x, bike.trail[i].y);
            ctx.stroke();
          }

          // Head glow
          const head = bike.trail[bike.trail.length - 1];
          const glow = ctx.createRadialGradient(head.x, head.y, 0, head.x, head.y, 12);
          glow.addColorStop(0, `rgba(${r},${g},${b},0.3)`);
          glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(head.x, head.y, 12, 0, Math.PI * 2);
          ctx.fill();

          // Bright dot at head
          ctx.fillStyle = `rgba(${r},${g},${b},0.6)`;
          ctx.beginPath();
          ctx.arc(head.x, head.y, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    />
  );
}
